<template>
  <div
    class="row g-3 mb-4"
    aria-label="Vacation summary cards"
  >
    <div class="col-12 col-md-6 col-xl-4">
      <div
        class="card border-start border-primary border-3 h-100"
        aria-label="Vacation balance"
      >
        <div class="card-body">
          <div class="text-muted small text-uppercase mb-1">
            Vacation
          </div>
          <div
            v-if="vacationStats"
            class="d-flex justify-content-between align-items-center mb-2"
          >
            <span class="fs-4 fw-semibold">{{ vacationStats.used }} / {{ vacationStats.total }}</span>
            <span class="text-muted small">{{ vacationStats.balance }} days left</span>
          </div>
          <div
            v-else
            class="text-muted"
          >
            —
          </div>
          <div
            v-if="vacationStats"
            class="progress"
            role="progressbar"
            :aria-valuenow="vacationStats.percent"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-label="Vacation days used"
            style="height: 6px"
          >
            <div
              class="progress-bar"
              :class="progressBarClass(vacationStats.percent)"
              :style="{ width: Math.min(vacationStats.percent, 100) + '%' }"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 col-md-6 col-xl-4">
      <div
        class="card border-start border-warning border-3 h-100"
        aria-label="Sick leave balance"
      >
        <div class="card-body">
          <div class="text-muted small text-uppercase mb-1">
            Sick Leave
          </div>
          <div
            v-if="sickStats"
            class="d-flex justify-content-between align-items-center mb-2"
          >
            <span class="fs-4 fw-semibold">{{ sickStats.used }} / {{ sickStats.total }}</span>
            <span class="text-muted small">{{ sickStats.balance }} days left</span>
          </div>
          <div
            v-else
            class="text-muted"
          >
            —
          </div>
          <div
            v-if="sickStats"
            class="progress"
            role="progressbar"
            :aria-valuenow="sickStats.percent"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-label="Sick leave days used"
            style="height: 6px"
          >
            <div
              class="progress-bar"
              :class="progressBarClass(sickStats.percent)"
              :style="{ width: Math.min(sickStats.percent, 100) + '%' }"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 col-md-6 col-xl-4">
      <div
        class="card border-start border-info border-3 h-100"
        aria-label="Pending requests"
      >
        <div class="card-body">
          <div class="text-muted small text-uppercase mb-1">
            Pending
          </div>
          <div class="fs-4 fw-semibold mb-2">
            {{ pendingCount }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
    name: "VacationDashboardSummary",
    props: {
        staffId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            currentYear: new Date().getFullYear(),
        };
    },
    computed: {
        vacationList() {
            return this.$store.getters["vacation/vacationList"];
        },
        sickLeaveList() {
            return this.$store.getters["vacation/sickLeaveList"];
        },
        vacationStats() {
            return this.computeYearStats(this.vacationList, "vacation");
        },
        sickStats() {
            return this.computeYearStats(this.sickLeaveList, "sick");
        },
        pendingCount() {
            const isPending = (v) => v.is_approved !== 1 && !v.is_rejected;
            return this.vacationList.filter(isPending).length + this.sickLeaveList.filter(isPending).length;
        },
    },
    methods: {
        computeYearStats(list, type) {
            if (!list || list.length === 0) return null;
            const yearly = this.groupByYear(list);
            const yearData = yearly[this.currentYear] || yearly[Object.keys(yearly).sort().pop()];
            if (!yearData) return null;
            const total = type === "vacation"
                ? yearData[0].total_vacation_days
                : yearData[0].total_sick_leave_days;
            let used = 0;
            for (const v of yearData) {
                const days = v._daysInYear ?? v.total_days;
                if (v.is_approved === 1) used += days;
            }
            const balance = total - used;
            const percent = total > 0 ? (used / total) * 100 : 0;
            return { used, total, balance, percent };
        },
        groupByYear(list) {
            const yearly = {};
            for (const v of list) {
                const startYear = v.start_year;
                const endYear = String(v.end_year);
                const startDays = parseFloat(v.days_in_start_year) || 0;
                const endDays = parseFloat(v.days_in_end_year) || 0;
                if (!yearly[startYear]) yearly[startYear] = [];
                yearly[startYear].push({ ...v, _yearContext: startYear, _daysInYear: startDays });
                if (startYear !== endYear && endDays > 0) {
                    if (!yearly[endYear]) yearly[endYear] = [];
                    yearly[endYear].push({ ...v, _yearContext: endYear, _daysInYear: endDays });
                }
            }
            return yearly;
        },
        progressBarClass(percent) {
            if (percent >= 90) return "bg-danger";
            if (percent >= 70) return "bg-warning";
            return "bg-success";
        },
    },
};
</script>
